/**
 * Design Agent Preview Component
 *
 * Displays the generated HTML in an iframe with live reload capability.
 * Supports version history management and full design controls.
 */

import React, { useEffect, useState, useCallback } from 'react';
// Lucide icons replaced by @ui-library/Icon
import api from '../utils/axios';
import { Button, Text, Icon, Container } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { IfHasPermission } from './IfHasPermission';
import { Permissions } from '../constants';
import { DataModelObject } from '../types/dataModel';
import { PlatformType } from '../types/project';

interface DesignAgentPreviewProps {
  projectId: string;
  refreshTrigger?: boolean;
  project?: { permissions?: string[]; platform_type?: string } | null;
  onClose?: () => void;
}

interface HtmlVersion {
  filename: string;
  timestamp: number;
  size: number;
}

const DesignAgentPreview: React.FC<DesignAgentPreviewProps> = ({
  projectId,
  refreshTrigger,
  project,
  onClose
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [htmlVersions, setHtmlVersions] = useState<HtmlVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [isRevertingVersion, setIsRevertingVersion] = useState(false);
  const [isRefreshingPreview, setIsRefreshingPreview] = useState(false);
  const [designPreviewRefresh, setDesignPreviewRefresh] = useState<number>(0);

  const fetchHtmlVersions = useCallback(async () => {
    if (!projectId) return;

    try {
      const response = await api.get(`/projects/${projectId}/design-agent/versions`);
      setHtmlVersions(response.data.versions || []);
    } catch (err) {
      console.error('Failed to fetch HTML versions:', err);
      setHtmlVersions([]);
    }
  }, [projectId]);

  const fetchPreview = useCallback(async () => {
    try {
      setLoading(true);
      let response;
      if (selectedVersion) {
        // Fetch specific version
        response = await api.get(`/projects/${projectId}/design-agent/versions/${selectedVersion}`);
        setHtmlContent(response.data.html_content || '');
      } else {
        // Fetch current version
        response = await api.get(`/projects/${projectId}/design-agent/preview`);
        setHtmlContent(response.data.html_content || '');
      }
    } catch (error) {
      console.error('Failed to fetch design preview:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedVersion]);

  // Fetch versions on mount and when triggers change
  useEffect(() => {
    fetchHtmlVersions();
  }, [projectId, refreshTrigger, designPreviewRefresh, fetchHtmlVersions]);

  // Fetch preview content when triggers or selection changes
  useEffect(() => {
    fetchPreview();
  }, [projectId, refreshTrigger, designPreviewRefresh, selectedVersion, fetchPreview]);

  const formatVersionTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleVersionSelect = (timestamp: number) => {
    setSelectedVersion(timestamp);
    setShowVersionDropdown(false);
  };

  const handleClearVersionSelection = () => {
    setSelectedVersion(null);
  };

  const handleRevertToVersion = async (timestamp: number) => {
    if (!projectId) return;

    const confirmed = window.confirm(
      `Are you sure you want to revert to the version from ${formatVersionTimestamp(timestamp)}?\n\n` +
      'This will:\n' +
      '1. Make this version the current index.html\n' +
      '2. Delete all versions created after this timestamp\n\n' +
      'This action cannot be undone.'
    );

    if (!confirmed) return;

    setIsRevertingVersion(true);

    try {
      await api.post(`/projects/${projectId}/design-agent/versions/${timestamp}/revert`);

      // Clear selected version and refresh
      setSelectedVersion(null);
      setShowVersionDropdown(false);

      // Refresh versions list and preview
      await fetchHtmlVersions();
      setDesignPreviewRefresh(prev => prev + 1);

      alert('Successfully reverted to selected version');
    } catch (error: any) {
      console.error('Failed to revert to version:', error);
      alert(error.response?.data?.detail || 'Failed to revert to version');
    } finally {
      setIsRevertingVersion(false);
    }
  };

  const handleDeleteHtml = async () => {
    if (!projectId) return;

    // Show confirmation dialog with version count
    const versionCount = htmlVersions.length;
    const totalFiles = versionCount + 1; // +1 for current index.html

    const confirmed = window.confirm(
      `Are you sure you want to delete the generated Mock Up?\n\n` +
      `This will delete:\n` +
      `• Current version\n` +
      `• ${versionCount} saved version${versionCount !== 1 ? 's' : ''}\n\n` +
      `Total: ${totalFiles} file${totalFiles !== 1 ? 's' : ''}\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const response = await api.delete(`/projects/${projectId}/design-agent/html`);

      // Clear state
      setSelectedVersion(null);
      setHtmlVersions([]);
      setShowVersionDropdown(false);
      setHtmlContent('');

      // Refresh preview after deletion
      setDesignPreviewRefresh(prev => prev + 1);

      // Show success message with count
      const deletedCount = response.data.deleted_files?.length || totalFiles;
      alert(`Successfully deleted ${deletedCount} file${deletedCount !== 1 ? 's' : ''}`);

    } catch (error: any) {
      console.error('Failed to delete HTML file:', error);
      alert(error.response?.data?.detail || 'Failed to delete HTML file');
    }
  };

  const handleDownloadHtml = async () => {
    if (!projectId) return;

    try {
      const response = await api.get(`/projects/${projectId}/design-agent/download`, {
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'design.html'); // Could be improved with project title if passed in props
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download HTML file:', error);
      alert(error.response?.data?.detail || 'Failed to download HTML file');
    }
  };

  const handleRefreshPreview = () => {
    setIsRefreshingPreview(true);
    setDesignPreviewRefresh(prev => prev + 1);
    setTimeout(() => setIsRefreshingPreview(false), 1000);
  };

  const handleOpenInNewTab = () => {
    if (!projectId) return;
    const url = `/project/${projectId}/design`;
    window.open(url, '_blank');
  };

  const renderVersionHistory = () => {
    if (htmlVersions.length === 0) return null;

    return (
      <>
        {showVersionDropdown && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowVersionDropdown(false)}
          />
        )}
        <div className="relative" style={{ zIndex: 50 }}>
          <Button
            onClick={() => setShowVersionDropdown(!showVersionDropdown)}
            variant='ghost'
            size='small'
            className={`!p-bg-v2-md !rounded-bg-v2-md transition-colors relative ${(selectedVersion || showVersionDropdown) ? '!bg-bg-primary-hover' : ''}`}
            title="Version History"
            label={
              <>
                <Icon
                  name={IconName.History}
                  size='sm'
                  variant='secondary'
                />
                {htmlVersions.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-utility-brand-500 text-text-white text-bg-v2-xs rounded-bg-v2-full w-4 h-4 flex items-center justify-center font-bg-v2-medium">
                    {htmlVersions.length}
                  </span>
                )}
              </>
            }
          />

          {showVersionDropdown && (
            <div className="absolute right-0 mt-2 w-96 bg-bg-primary rounded-lg shadow-bg-v2-xl border border-border-secondary z-[60] max-h-[32rem] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-bg-v2-md border-b border-border-secondary bg-bg-primary-alt">
                <div className="flex items-center justify-between">
                  <div>
                    <Text variant='text-md' weight='semibold' color='primary'>Version History</Text>
                    <Text variant='text-xs' color='secondary' className='mt-0.5'>
                      {htmlVersions.length + 1} version{htmlVersions.length !== 0 ? 's' : ''} available
                    </Text>
                  </div>
                  {selectedVersion && (
                    <Button
                      onClick={handleClearVersionSelection}
                      variant='outline'
                      size='small'
                      className="!border-utility-blue-200 !text-utility-blue-700 hover:!bg-utility-blue-50"
                      label="View Current"
                    />
                  )}
                </div>
              </div>

              {/* Version List */}
              <div className="flex-1 overflow-y-auto">
                {/* Current Version (always first) */}
                <div
                  className={`px-bg-v2-lg py-bg-v2-md border-b border-border-secondary transition-colors ${!selectedVersion
                    ? 'bg-utility-blue-100 border-l-4 border-l-utility-blue-500'
                    : 'hover:bg-bg-primary-hover border-l-4 border-l-transparent'
                    }`}
                >
                  <div className="flex items-start gap-bg-v2-md">
                    {/* Timeline dot */}
                    <div className="flex flex-col items-center pt-1">
                      <div className={`w-3 h-3 rounded-full border-2 ${!selectedVersion
                        ? 'bg-utility-blue-500 border-utility-blue-600'
                        : 'bg-bg-primary border-border-primary'
                        }`}></div>
                      {htmlVersions.length > 0 && (
                        <div className="w-0.5 h-full bg-border-secondary mt-1"></div>
                      )}
                    </div>

                    {/* Version Info */}
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={handleClearVersionSelection}
                        className="text-left w-full group"
                      >
                        <div className="flex items-center gap-2">
                          <Text variant='text-sm' weight='semibold' color='primary' className="group-hover:text-utility-blue-600">
                            Current Version
                          </Text>
                          {!selectedVersion && (
                            <span className="px-2 py-0.5 text-bg-v2-xs font-bg-v2-medium bg-utility-blue-200 text-utility-blue-800 rounded-bg-v2-sm">
                              Viewing
                            </span>
                          )}
                        </div>
                        <Text variant='text-xs' color='secondary' className="mt-1">
                          Active version shown to users
                        </Text>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Historical Versions */}
                {htmlVersions.map((version, index) => {
                  const isSelected = selectedVersion === version.timestamp;

                  return (
                    <div
                      key={version.timestamp}
                      className={`px-bg-v2-lg py-bg-v2-md border-b border-border-secondary last:border-b-0 transition-colors ${isSelected
                        ? 'bg-utility-blue-100 border-l-4 border-l-utility-blue-500'
                        : 'hover:bg-bg-primary-hover border-l-4 border-l-transparent'
                        }`}
                    >
                      <div className="flex items-start gap-bg-v2-md">
                        {/* Timeline dot */}
                        <div className="flex flex-col items-center pt-1">
                          <div className={`w-3 h-3 rounded-full border-2 ${isSelected
                            ? 'bg-utility-blue-500 border-utility-blue-600'
                            : 'bg-bg-primary border-border-primary'
                            }`}></div>
                          {index < htmlVersions.length - 1 && (
                            <div className="w-0.5 h-full bg-border-secondary mt-1"></div>
                          )}
                        </div>

                        {/* Version Info */}
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleVersionSelect(version.timestamp)}
                            className="text-left w-full group"
                          >
                            <div className="flex items-center gap-2">
                              <Text variant='text-sm' weight='semibold' color='primary' className="group-hover:text-utility-blue-600">
                                {formatVersionTimestamp(version.timestamp)}
                              </Text>
                              {isSelected && (
                                <span className="px-2 py-0.5 text-bg-v2-xs font-bg-v2-medium bg-utility-blue-200 text-utility-blue-800 rounded-bg-v2-sm">
                                  Viewing
                                </span>
                              )}
                            </div>
                            <Text variant='text-xs' color='secondary' className="mt-1">
                              Size: {(version.size / 1024).toFixed(1)} KB
                            </Text>
                          </button>

                          {/* Revert Button - only show if user can modify project */}
                          <IfHasPermission
                            permissions={project?.permissions}
                            requires={Permissions.UpdateProjectPermission}
                          >
                            <Button
                              onClick={() => handleRevertToVersion(version.timestamp)}
                              disabled={isRevertingVersion}
                              variant='outline'
                              size='small'
                              className="mt-2 w-full !justify-center !bg-utility-blue-50 hover:!bg-utility-blue-100 !border-utility-blue-200 !text-utility-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              label={
                                <div className="flex items-center gap-1.5">
                                  <Icon name={IconName.Rotate90Degrees} size='xs' variant='secondary' />
                                  Revert to this version
                                </div>
                              }
                            />
                          </IfHasPermission>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer Info */}
              <div className="px-bg-v2-lg py-bg-v2-md bg-bg-primary-alt border-t border-border-secondary">
                <Text variant='text-xs' color='secondary'>
                  💡 Click a version to preview it. Use "Revert" to make it the current version.
                </Text>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderActionButtons = () => {
    // Don't show action buttons if there's no HTML content
    if (!htmlContent) return null;

    return (
      <div className="flex items-center gap-2 mr-4">
        {renderVersionHistory()}

        <IfHasPermission
          permissions={project?.permissions}
          requires={Permissions.UpdateProjectPermission}
        >
          <Button
            onClick={handleDeleteHtml}
            variant='ghost'
            size='small'
            className="!p-bg-v2-md !rounded-bg-v2-md hover:bg-bg-error-primary"
            title="Delete HTML file"
            label={<Icon name={IconName.Delete} size='sm' variant='error' />}
          />
        </IfHasPermission>
        <IfHasPermission
          permissions={project?.permissions}
          requires={Permissions.DownloadPermission}
        >
          <Button
            onClick={handleDownloadHtml}
            variant='ghost'
            size='small'
            className="!p-bg-v2-md !rounded-bg-v2-md"
            title="Download HTML file"
            label={<Icon name={IconName.Download} size='sm' variant='secondary' />}
          />
        </IfHasPermission>
        <Button
          onClick={handleRefreshPreview}
          disabled={isRefreshingPreview}
          variant='ghost'
          size='small'
          className="!p-bg-v2-md !rounded-bg-v2-md disabled:opacity-50"
          title="Refresh preview"
          label={<Icon name={IconName.Reload} size='sm' variant='secondary' spin={isRefreshingPreview} />}
        />
        <Button
          onClick={handleOpenInNewTab}
          variant='ghost'
          size='small'
          className="!p-bg-v2-md !rounded-bg-v2-md"
          title="Open in new tab"
          label={<Icon name={IconName.ExternalLink} size='sm' variant='secondary' />}
        />
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className='flex items-center justify-between min-h-[60px] max-h-[60px] px-bg-v2-xl bg-bg-primary border-b border-border-secondary'>
        <div className='flex items-center gap-bg-v2-lg flex-1'>
          <Icon name={IconName.Layout} size='sm' variant='secondary' />
          <Text variant='text-sm' weight='semibold' color='primary'>
            Prototype Preview
          </Text>
        </div>

        {renderActionButtons()}

        {onClose && (
          <Button
            onClick={onClose}
            variant='ghost'
            size='small'
            className='!w-6 !h-6 !p-0 !rounded-full text-fg-secondary hover:text-fg-secondary-hover flex-shrink-0'
            aria-label='Close'
            label={<Icon name={IconName.Cross} size='sm' variant='secondary' />}
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="flex-1 min-h-0 bg-bg-secondary overflow-hidden relative">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Icon name={IconName.Loading} size='xl' spin className="mx-auto mb-2 text-utility-brand-500" inheritColor />
              <Text variant='text-sm' color='secondary'>Loading preview...</Text>
            </div>
          </div>
        ) : htmlContent ? (
          <iframe
            srcDoc={htmlContent}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
            title="Prototype Preview"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md mx-auto p-8">
              <div className="text-text-tertiary mb-4">
                <Icon name={IconName.Preview} size='xl' variant='secondary' />
              </div>
              <Text variant='text-lg' weight='medium' color='primary' className="mb-2" align='center'>
                No Prototype Yet
              </Text>
              <Text variant='text-sm' color='secondary' align='center'>
                {project?.platform_type === PlatformType.ServiceNow
                  ? 'Start chatting with the Prototype Agent to generate your ServiceNow interface. The generated data model will be used in Build Mode.'
                  : project?.platform_type === PlatformType.PowerApps
                  ? 'Start chatting with the Prototype Agent to generate your PowerApps interface. The generated data model will be used in Build Mode.'
                  : project?.platform_type === PlatformType.Salesforce
                  ? 'Start chatting with the Prototype Agent to generate your Salesforce interface. The generated data model will be used in Build Mode.'
                  : 'Start chatting with the Prototype Agent to generate your TAP interface. Your design will appear here as soon as it\'s generated.'}
              </Text>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Container className='rounded-bg-v2-2xl shadow-bg-v2-md border border-border-secondary h-full flex flex-col overflow-hidden' padding='none'>
      {renderHeader()}
      {renderContent()}
    </Container>
  );
};

export default DesignAgentPreview;
